import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { companyInfo, contactReasons } from '../data/mock';
import { toast } from 'sonner';
import { Mail, MapPin, Send, CheckCircle, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', reason: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to submit');

      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch shortly.');
      
      setTimeout(() => {
        setFormData({ name: '', email: '', phone: '', company: '', reason: '', message: '' });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#f5f5f7]">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="badge-minimal mb-6">
            Get In Touch
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-6">
            Start Your <span className="bg-gradient-to-r from-[#1C4B8A] to-[#2A5F9E] bg-clip-text text-transparent">Project</span> Today
          </h2>
          <p className="text-lg text-[#6e6e73] max-w-2xl mx-auto">
            Ready to build your cleanroom? Contact our team of experts for a consultation.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Contact Info - Dark Glass Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div 
              className="h-full rounded-3xl p-8 text-white relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #1C4B8A 0%, #0F2847 100%)',
                boxShadow: '0 25px 50px -12px rgba(28, 75, 138, 0.4)'
              }}
            >
              {/* Glass Decorations */}
              <div 
                className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-20"
                style={{
                  background: 'radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)',
                  transform: 'translate(30%, -30%)'
                }}
              />
              <div 
                className="absolute bottom-0 left-0 w-40 h-40 rounded-full opacity-15"
                style={{
                  background: 'radial-gradient(circle, rgba(138, 189, 224, 0.4) 0%, transparent 70%)',
                  transform: 'translate(-30%, 30%)'
                }}
              />
              
              <h3 className="text-xl font-bold text-white mb-8 relative z-10">Contact Information</h3>
              
              <div className="space-y-6 relative z-10">
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.15)'
                    }}
                  >
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm mb-1">Address</div>
                    <div className="text-white/70 text-sm">{companyInfo.address}</div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.15)'
                    }}
                  >
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm mb-1">Email</div>
                    <div className="text-white/70 text-sm">{companyInfo.email}</div>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-white/10 relative z-10">
                <div className="text-white/50 text-xs font-medium mb-2">Established</div>
                <div className="text-5xl font-bold">{companyInfo.founded}</div>
              </div>
            </div>
          </motion.div>

          {/* Form - Glass Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3 rounded-3xl p-8"
            style={{
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(40px)',
              WebkitBackdropFilter: 'blur(40px)',
              border: '1px solid rgba(255, 255, 255, 0.9)',
              boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08)'
            }}
          >
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-full flex flex-col items-center justify-center py-12 text-center"
                >
                  <div 
                    className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                    style={{
                      background: 'rgba(34, 197, 94, 0.1)',
                      border: '1px solid rgba(34, 197, 94, 0.2)'
                    }}
                  >
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">Message Sent!</h3>
                  <p className="text-[#6e6e73]">We'll get back to you within 24 hours.</p>
                </motion.div>
              ) : (
                <motion.form key="form" onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#1a1a1a] mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 text-[#1a1a1a] rounded-xl outline-none transition-all"
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1a1a1a] mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 text-[#1a1a1a] rounded-xl outline-none transition-all"
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#1a1a1a] mb-2">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-5 py-4 text-[#1a1a1a] rounded-xl outline-none transition-all"
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1a1a1a] mb-2">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-5 py-4 text-[#1a1a1a] rounded-xl outline-none transition-all"
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                        placeholder="Company Name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#1a1a1a] mb-2">Project Type *</label>
                    <select
                      name="reason"
                      value={formData.reason}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 text-[#1a1a1a] rounded-xl outline-none transition-all"
                      style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(0, 0, 0, 0.08)'
                      }}
                    >
                      <option value="">Select type</option>
                      {contactReasons.map((reason) => (
                        <option key={reason} value={reason}>{reason}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#1a1a1a] mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-5 py-4 text-[#1a1a1a] rounded-xl outline-none transition-all resize-none"
                      style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(0, 0, 0, 0.08)'
                      }}
                      placeholder="Tell us about your project..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 text-white font-semibold rounded-full transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-3 disabled:opacity-70"
                    style={{
                      background: 'linear-gradient(135deg, #1C4B8A 0%, #153A6B 100%)',
                      boxShadow: '0 4px 20px rgba(28, 75, 138, 0.4)'
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
